/**
 * @file BlogArticleTab.tsx
 * @description Вкладка «Статья» панели «Блог» (phase19.16 Р2; uiux-design-r2
 * §8; SB40, SB61): обложка [Широкая][Узкая][Без обложки], оглавление (при
 * 3+ подзаголовках) [Сбоку на ПК][Над текстом], «Поделиться», блок автора,
 * похожие статьи и сколько. На сайте — после публикации вида.
 *
 * SB122 (uiux-settings-groups §5, шаг 6; §8 — вид карточек): три
 * группы-карточки в `SettingsGroups panel="blog-article"` — «Вид статьи»
 * (обложка и оглавление), «Поделиться и автор», «Похожие статьи»; групп
 * меньше четырёх — оглавления-пилюль нет. Память свёрнутых —
 * `commora.settings-groups.blog-article`.
 *
 * Обложка и место оглавления — карточки-схемы `OptionCards` (замечание
 * пользователя 2026-10-12 «Нужна визуализация»; схемы — `BlogSchemes`).
 */

import type { SiteBlogOptions } from "@chaiberi/shared";

import { FieldHint, SettingsGroup, SettingsGroups } from "../../ui/settings-groups";
import { CheckRow } from "../CheckRow";
import { GroupField } from "../GroupField";
import { OptionCards } from "../OptionCards";
import { Segmented } from "../Segmented";
import { ArticleLayoutScheme, TocPlaceScheme } from "./BlogSchemes";

interface Props {
  options: SiteBlogOptions;
  onChange: (options: SiteBlogOptions) => void;
}

/** Шаблоны статьи: подпись карточки и слово для сводки. */
const LAYOUT = [
  { value: "wide" as const, label: "Широкая обложка", short: "обложка широкая" },
  { value: "narrow" as const, label: "Узкая обложка", short: "обложка узкая" },
  { value: "none" as const, label: "Без обложки", short: "без обложки" },
];

const TOC_PLACE = [
  { value: "side" as const, label: "Сбоку на ПК", short: "сбоку" },
  { value: "top" as const, label: "Над текстом", short: "над текстом" },
];

const RELATED_COUNTS = ["2", "3", "4", "6"].map((v) => ({ value: v, label: v }));

/** Сводка «Вида статьи»: обложка и где оглавление. */
function lookSummary(o: SiteBlogOptions): string {
  const cover = LAYOUT.find((l) => l.value === o.articleLayout)?.short ?? "";
  const toc = o.toc.on ? `оглавление ${TOC_PLACE.find((t) => t.value === o.toc.place)?.short ?? ""}` : "без оглавления";
  return `${cover} · ${toc}`;
}

export function BlogArticleTab({ options, onChange }: Props) {
  const extras = [options.share ? "поделиться" : null, options.authorBlock ? "автор" : null].filter(Boolean);
  return (
    <SettingsGroups panel="blog-article">
      <SettingsGroup id="look" title="Вид статьи" hint="Обложка и оглавление страницы статьи." summary={lookSummary(options)}>
        <GroupField label="Обложка">
          <OptionCards
            aria-label="Обложка статьи"
            columns={2}
            value={options.articleLayout}
            onChange={(articleLayout) => onChange({ ...options, articleLayout })}
            options={LAYOUT.map((l) => ({ value: l.value, label: l.label, preview: <ArticleLayoutScheme layout={l.value} /> }))}
          />
        </GroupField>
        <div className="flex min-w-0 flex-col gap-2">
          <CheckRow checked={options.toc.on} onChange={(on) => onChange({ ...options, toc: { ...options.toc, on } })}>
            Оглавление — при трёх и больше подзаголовках
          </CheckRow>
          {options.toc.on ? (
            <OptionCards
              aria-label="Где оглавление"
              columns={2}
              value={options.toc.place}
              onChange={(place) => onChange({ ...options, toc: { ...options.toc, place } })}
              options={TOC_PLACE.map((t) => ({ value: t.value, label: t.label, preview: <TocPlaceScheme place={t.value} /> }))}
            />
          ) : null}
        </div>
      </SettingsGroup>

      <SettingsGroup id="extras" title="Поделиться и автор" tocLabel="Автор" hint="Кнопки соцсетей и рассказ об авторе." summary={extras.length > 0 ? extras.join(" · ") : "нет"}>
        <div className="flex min-w-0 flex-col">
          <CheckRow checked={options.share} onChange={(share) => onChange({ ...options, share })}>
            Кнопки «Поделиться»
          </CheckRow>
          <CheckRow checked={options.authorBlock} onChange={(authorBlock) => onChange({ ...options, authorBlock })}>
            Блок автора
          </CheckRow>
        </div>
      </SettingsGroup>

      <SettingsGroup
        id="related"
        title="Похожие статьи"
        tocLabel="Похожие"
        hint="Подборка других статей блога после текста."
        summary={options.related.on ? `по ${options.related.count}` : "нет"}
      >
        <CheckRow checked={options.related.on} onChange={(on) => onChange({ ...options, related: { ...options.related, on } })}>
          Похожие статьи
        </CheckRow>
        {options.related.on ? (
          <GroupField label="Сколько статей">
            <Segmented
              aria-label="Сколько похожих статей"
              options={RELATED_COUNTS}
              value={String(options.related.count)}
              onChange={(v) => onChange({ ...options, related: { ...options.related, count: Number(v) } })}
            />
          </GroupField>
        ) : (
          <FieldHint className="mt-0">Выключено — подборки других статей после текста нет.</FieldHint>
        )}
      </SettingsGroup>
    </SettingsGroups>
  );
}
